import { useQuery } from "@tanstack/react-query";
import { fetcher } from "../../../utils/Axios";
import {
  CASE_DETAIL,
  GET_CASE_DETAIL_QUERY_KEY,
  VOICE_NOTE,
  VOICE_NOTE_REVISION_LOG,
} from "../types/CommonTypes";
import { queryClient } from "../../../utils/ReactQueryConfig";
export const caseDetailKey = (
  tenantId?: string,
  hospitalId?: string,
  caseId?: number
) => [GET_CASE_DETAIL_QUERY_KEY, tenantId, hospitalId, caseId];
async function getCaseDetail(
  tenantId?: string,
  hospitalId?: string,
  caseId?: number
): Promise<CASE_DETAIL> {
  console.log({ tenantId, hospitalId, caseId });
  const { data } = await fetcher({
    url: `case/${caseId}`,
    headers: {
      "x-hospital-id": hospitalId,
      "x-tenant-id": tenantId,
    },
  });
  return data;
}

export default function useGetCaseDetailQuery(
  tenantId?: string,
  hospitalId?: string,
  caseId?: number,
  enabled: boolean = true
) {
  return useQuery({
  queryKey: caseDetailKey(tenantId, hospitalId, caseId),
    queryFn: () => getCaseDetail(tenantId, hospitalId, caseId),
    enabled: !!tenantId && !!hospitalId && !!caseId && enabled,
  });
}


export const updateCaseDetailInCache = (updateData: {
  caseId: number;
  tenantId?: string;
  hospitalId?: string;
  data: Partial<CASE_DETAIL>;
}) => {
  const { caseId, tenantId, hospitalId, data } = updateData;

  queryClient.setQueryData(
    caseDetailKey(tenantId, hospitalId, caseId),
    (oldData?: CASE_DETAIL) => (oldData ? { ...oldData, ...data } : oldData)
  );
};

export const updateCaseVoiceNoteDetailInCache = (updateData: {
  caseId: number;
  tenantId?: string;
  hospitalId?: string;
  data: Partial<VOICE_NOTE>;
}) => {
  const buildVoiceNoteRevisionLogs = (
    note: VOICE_NOTE,
    data: Partial<VOICE_NOTE>,
  ): Array<VOICE_NOTE_REVISION_LOG> | undefined => {
    if (Array.isArray(data.revisionLogs)) {
      return data.revisionLogs;
    }

    const nextNote = data.note;
    const hasNoteChanged =
      typeof nextNote === "string" && nextNote !== note.note;

    if (!hasNoteChanged) {
      return note.revisionLogs;
    }

    const createdAt = data.updatedAt ?? new Date().toISOString();
    const existingLogs = note.revisionLogs ?? [];

    return [
      {
        id: `${note.id}-${createdAt}`,
        caseNoteId: note.id,
        caseId: note.caseId,
        previousNote: note.note,
        revisedNote: nextNote,
        action: "UPDATE",
        revisedByUserId: data.loggedById ?? note.loggedById,
        revisedByUserName: data.loggedBy ?? note.loggedBy,
        createdAt,
      },
      ...existingLogs,
    ];
  };

  const { caseId, tenantId, hospitalId, data } = updateData;

  queryClient.setQueryData(
    caseDetailKey(tenantId, hospitalId, caseId),
    (oldData?: CASE_DETAIL) => {
      if (!oldData) return oldData;

      return {
        ...oldData,
        caseNotes: oldData.caseNotes?.map((note) =>
          note?.id === data?.id
            ? {
                ...note,
                ...data,
                revisionLogs: buildVoiceNoteRevisionLogs(note, data),
              }
            : note
        ),
      };
    }
  );
};

export const refetchCaseDetailQuery = (
  caseId?: number | null,
  tenantId?: string,
  hospitalId?: string
) => {
  if (!caseId) return;

  queryClient.refetchQueries({
    queryKey: caseDetailKey(tenantId, hospitalId, caseId),
  });
};
